import React, { useState, useContext, useEffect } from 'react';
import { AuthContext, supabase } from '../../context/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Auth.css';
import logger from '../../utils/logger';

const Login = () => {
  const { session } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const location = useLocation();

  // Redirect if user is already logged in
  useEffect(() => {
    if (session) {
      navigate('/dashboard');
    }
  }, [session, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          // Set remember me option
          rememberMe
        }
      });

      if (error) {
        throw error;
      }

      if (data?.user) {
        // Save login preference to localStorage
        localStorage.setItem('useremail', email);
        navigate('/dashboard');
      }
    } catch (err) {
      logger.error('Login error:', err);
      setError(err.message || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        setError(error.message);
      } else {
        setResetSent(true);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Toggle between login and reset password forms
  const toggleResetPassword = () => {
    setResetPassword(!resetPassword);
    setError(null);
    setResetSent(false);
  };

  // If showing reset password form
  if (resetPassword) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Reset Your Password</h2>
          {resetSent ? (
            <div className="success-message">
              <p>Password reset email sent. Please check your inbox.</p>
              <button 
                onClick={toggleResetPassword} 
                className="auth-button"
                style={{ marginTop: '20px' }}
              >
                Back to Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label htmlFor="reset-email">Email</label>
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <button 
                type="submit" 
                className="auth-button" 
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <div className="auth-footer">
                <button 
                  type="button" 
                  onClick={toggleResetPassword} 
                  className="text-button"
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Regular login form
  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Login to Advantis AI</h2>
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
            />
          </div>
          <div className="form-options">
            <div className="checkbox-group">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="remember-me">Remember me</label>
            </div>
            <button 
              type="button" 
              onClick={toggleResetPassword} 
              className="text-button"
            >
              Forgot password?
            </button>
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <div className="auth-footer">
          Don't have an account?{' '}
          <Link to="/signup" className="auth-link">
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login; 